"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.addFooter = exports.addProductComparisonTable = exports.addProductComparisonImagesTable = exports.addMultiLineText = exports.addHeader = void 0;
const jspdf_autotable_1 = require("jspdf-autotable");
const product_page_print_constants_1 = require("../constants/product-page-print-constants");
const product_compare_print_model_1 = require("../model/product-compare-print.model");
const addHeader = (doc, logoImage, title, currentX, currentY) => {
    if (!!logoImage) {
        doc.addImage(logoImage, "JPEG", currentX, currentY, product_page_print_constants_1.LOGO_WIDTH, product_page_print_constants_1.LOGO_HEIGHT);
    }
    currentX += product_page_print_constants_1.LOGO_WIDTH + 20;
    const textWidth = product_page_print_constants_1.PAGE_WIDTH - currentX - product_page_print_constants_1.PAGE_MARGIN;
    (0, exports.addMultiLineText)(doc, title, currentX, currentY, textWidth);
};
exports.addHeader = addHeader;
const addMultiLineText = (doc, text, currentX, currentY, textWidth, fontSize = product_page_print_constants_1.HEADING_FONT_SIZE, fontStyle = product_page_print_constants_1.JSPDF_TYPE_BOLD, textColor = product_page_print_constants_1.BLACK) => {
    currentY += fontSize * product_page_print_constants_1.TEXT_LINE_HEIGHT;
    doc.setFont(product_page_print_constants_1.FONT_NAME, fontStyle);
    doc.setFontSize(fontSize);
    doc.setTextColor(textColor);
    const multiLineText = doc.splitTextToSize(text, textWidth);
    doc.text(multiLineText, currentX, currentY);
    currentY += fontSize * product_page_print_constants_1.TEXT_LINE_HEIGHT * (multiLineText.length - 1);
    return [currentX, currentY];
};
exports.addMultiLineText = addMultiLineText;
const addProductComparisonImagesTable = (doc, productImages, productImagesMap, defaultProductImageUrl, currentX, currentY) => {
    const { firstColumnWidth, productImageSize } = product_page_print_constants_1.PRODUCT_COMPARE_DIMENSIONS;
    const columnWidth = (product_page_print_constants_1.PAGE_CONTENT_WIDTH - firstColumnWidth) / productImages?.length;
    const columnStyles = {
        0: { cellWidth: firstColumnWidth },
    };
    const { cellPadding } = product_page_print_constants_1.PRODUCT_COMPARE_TABLE_STYLES;
    let productImageIndex = 0;
    (0, jspdf_autotable_1.default)(doc, {
        theme: product_page_print_constants_1.JSPDF_TYPE_PLAIN,
        body: [[""].concat(productImages.map(() => ""))],
        startY: currentY,
        margin: {
            top: product_page_print_constants_1.HEADER_HEIGHT,
            left: currentX,
        },
        tableWidth: product_page_print_constants_1.PAGE_CONTENT_WIDTH,
        columnStyles,
        styles: {
            ...product_page_print_constants_1.JSPDF_TABLE_BASE_STYLES,
            ...product_page_print_constants_1.PRODUCT_COMPARE_TABLE_STYLES,
        },
        rowPageBreak: "avoid",
        didParseCell: (data) => {
            data.cell.styles.minCellHeight = productImageSize;
        },
        didDrawCell: (data) => {
            if (data.column.index !== 0 &&
                productImageIndex < productImages?.length) {
                const image = productImagesMap?.get(productImages?.[productImageIndex]) ||
                    productImagesMap?.get(defaultProductImageUrl);
                if (!!image) {
                    doc.addImage(image, "JPEG", data.cursor.x + (columnWidth - productImageSize) / 2, data.cursor.y + cellPadding, productImageSize - cellPadding * 2, productImageSize - cellPadding * 2);
                }
                productImageIndex++;
            }
        },
    });
};
exports.addProductComparisonImagesTable = addProductComparisonImagesTable;
const addProductComparisonTable = (doc, includeProductPrices, rowLabels, productCompareDetails, productPills, productPillImageMap, currentX, currentY) => {
    const firstColumnWidth = product_page_print_constants_1.PRODUCT_COMPARE_DIMENSIONS.firstColumnWidth;
    const columnWidth = (product_page_print_constants_1.PAGE_CONTENT_WIDTH - firstColumnWidth) / productCompareDetails?.length;
    const columnStyles = {
        0: { cellWidth: firstColumnWidth },
    };
    for (let i = 1; i <= productCompareDetails?.length; i++) {
        columnStyles[i] = { cellWidth: columnWidth };
    }
    const productCompareTable = createCompareTableStructure(rowLabels);
    addDataToRows(productCompareDetails, productCompareTable, includeProductPrices);
    const tableHeader = createTableHeader(productCompareDetails, rowLabels);
    const productCompareTableBody = createTableBody(productCompareTable);
    [currentX, currentY] = addProductCompareTable(doc, tableHeader, productCompareTableBody, productPills, productPillImageMap, currentX, currentY, columnStyles, columnWidth);
    const nutritionTable = createNutritionTableStructure(rowLabels, productCompareDetails);
    addDataToRows(productCompareDetails, nutritionTable);
    const nutritionTableBody = createTableBody(nutritionTable);
    addNutritionTable(doc, tableHeader, nutritionTableBody, currentX, currentY, columnStyles);
};
exports.addProductComparisonTable = addProductComparisonTable;
const createTableHeader = (productCompareDetails, rowLabels) => {
    const headerStyle = {
        halign: product_page_print_constants_1.PRODUCT_COMPARE_TABLE_STYLES.halign,
        fontStyle: product_page_print_constants_1.JSPDF_TYPE_BOLD,
    };
    const headerFirstRow = [
        {
            content: rowLabels?.productNumber || "",
            styles: headerStyle,
        },
    ];
    const headerSecondRow = [
        {
            content: rowLabels?.productDescription || "",
            styles: headerStyle,
        },
    ];
    productCompareDetails.forEach((printProductCompareModel) => {
        headerFirstRow.push({
            content: `#${printProductCompareModel?.productDetail?.productNumber || ""}`,
            styles: headerStyle,
        });
        headerSecondRow.push({
            content: printProductCompareModel?.productDescription || "",
            styles: headerStyle,
        });
    });
    return [headerFirstRow, headerSecondRow];
};
const createCompareTableStructure = (rowLabels) => {
    const documentRows = [];
    let fillColor = product_page_print_constants_1.WHITE;
    const boldFontStyle = { fontStyle: product_page_print_constants_1.JSPDF_TYPE_BOLD };
    fillColor = addProductCompareRow(documentRows, product_compare_print_model_1.ProductCompareRowType.brandName, rowLabels?.brandName, fillColor, true, boldFontStyle);
    fillColor = addProductCompareRow(documentRows, product_compare_print_model_1.ProductCompareRowType.price, rowLabels?.price, fillColor, false, boldFontStyle);
    fillColor = addProductCompareRow(documentRows, product_compare_print_model_1.ProductCompareRowType.priceCases, rowLabels?.cases, fillColor, false);
    fillColor = addProductCompareRow(documentRows, product_compare_print_model_1.ProductCompareRowType.priceEaches, rowLabels?.eaches, fillColor, true);
    fillColor = addProductCompareRow(documentRows, product_compare_print_model_1.ProductCompareRowType.packSize, rowLabels?.packSize, fillColor, true, boldFontStyle);
    fillColor = addProductCompareRow(documentRows, product_compare_print_model_1.ProductCompareRowType.pricePerPortion, rowLabels?.pricePerPortion, fillColor, true, boldFontStyle);
    fillColor = addProductCompareRow(documentRows, product_compare_print_model_1.ProductCompareRowType.availableInventory, rowLabels?.availableInventory, fillColor, false, boldFontStyle);
    fillColor = addProductCompareRow(documentRows, product_compare_print_model_1.ProductCompareRowType.availableInventoryCases, rowLabels?.cases, fillColor, false);
    fillColor = addProductCompareRow(documentRows, product_compare_print_model_1.ProductCompareRowType.availableInventoryEaches, rowLabels?.eaches, fillColor, true);
    fillColor = addProductCompareRow(documentRows, product_compare_print_model_1.ProductCompareRowType.productAttributes, rowLabels?.productAttributes, fillColor, true, boldFontStyle);
    fillColor = addProductCompareRow(documentRows, product_compare_print_model_1.ProductCompareRowType.purchaseHistory, rowLabels?.purchaseHistory, fillColor, true, boldFontStyle);
    fillColor = addProductCompareRow(documentRows, product_compare_print_model_1.ProductCompareRowType.netWeight, rowLabels?.netWeight, fillColor, true, boldFontStyle);
    fillColor = addProductCompareRow(documentRows, product_compare_print_model_1.ProductCompareRowType.usfStorage, rowLabels?.usfStorage, fillColor, true, boldFontStyle);
    fillColor = addProductCompareRow(documentRows, product_compare_print_model_1.ProductCompareRowType.ingredients, rowLabels?.ingredients, fillColor, true, boldFontStyle, { halign: "left", valign: "top" });
    return documentRows;
};
const createNutritionTableStructure = (rowLabels, productCompareDetails) => {
    const documentRows = [];
    let fillColor = product_page_print_constants_1.WHITE;
    fillColor = addProductCompareRow(documentRows, product_compare_print_model_1.ProductCompareRowType.nutritionalFacts, rowLabels?.nutritionalFacts, fillColor, true, { fontStyle: product_page_print_constants_1.JSPDF_TYPE_BOLD });
    fillColor = addProductCompareRow(documentRows, product_compare_print_model_1.ProductCompareRowType.servingSize, "Serving Size", fillColor, true);
    fillColor = addProductCompareRow(documentRows, product_compare_print_model_1.ProductCompareRowType.servingsPerContainer, "Servings Per Container", fillColor, true);
    fillColor = addProductCompareRow(documentRows, product_compare_print_model_1.ProductCompareRowType.calories, "Calories", fillColor, true);
    if (productCompareDetails.some((product) => product?.productDetail?.isUsda)) {
        fillColor = addProductCompareRow(documentRows, product_compare_print_model_1.ProductCompareRowType.caloriesFromFat, "Calories From Fat", fillColor, true);
    }
    if (productCompareDetails?.length > 0) {
        const nutrients = productCompareDetails.some((product) => product?.productDetail?.isUsda)
            ? productCompareDetails.every((product) => product?.productDetail?.isUsda)
                ? product_page_print_constants_1.USDA_NUTRIENT_LABELS
                : product_page_print_constants_1.MERGED_NUTRIENT_LABELS
            : product_page_print_constants_1.FDA_NUTRIENT_LABELS;
        for (const nutrient of nutrients) {
            fillColor = addProductCompareRow(documentRows, product_compare_print_model_1.ProductCompareRowType.nutrient, nutrient, fillColor, true);
        }
    }
    return documentRows;
};
const addProductCompareRow = (rows, type, header, fillColor = product_page_print_constants_1.WHITE, toggleFillColor = false, headerStyles, valueStyles, rowStyles) => {
    rows.push({
        type,
        header,
        values: [],
        headerStyles,
        valueStyles,
        rowStyles: {
            ...rowStyles,
            fillColor,
        },
    });
    if (toggleFillColor) {
        return fillColor === product_page_print_constants_1.WHITE ? product_page_print_constants_1.LIGHT_GRAY : product_page_print_constants_1.WHITE;
    }
    else {
        return fillColor;
    }
};
const addDataToRows = (productCompareDetails, documentRows, includeProductPrices = true) => {
    productCompareDetails?.forEach((printProductCompareModel) => {
        documentRows?.forEach((row) => {
            let value = undefined;
            switch (row?.type) {
                case product_compare_print_model_1.ProductCompareRowType.brandName:
                    value = printProductCompareModel?.brandName;
                    break;
                case product_compare_print_model_1.ProductCompareRowType.price:
                    value = "";
                    break;
                case product_compare_print_model_1.ProductCompareRowType.priceCases:
                    if (includeProductPrices) {
                        value = printProductCompareModel?.priceCases;
                    }
                    break;
                case product_compare_print_model_1.ProductCompareRowType.priceEaches:
                    if (includeProductPrices) {
                        value = printProductCompareModel?.priceEaches;
                    }
                    break;
                case product_compare_print_model_1.ProductCompareRowType.packSize:
                    value =
                        printProductCompareModel?.productDetail?.summary?.salesPackSize;
                    break;
                case product_compare_print_model_1.ProductCompareRowType.pricePerPortion:
                    if (includeProductPrices) {
                        value = printProductCompareModel?.pricePerPortion;
                    }
                    break;
                case product_compare_print_model_1.ProductCompareRowType.availableInventory:
                    value = "";
                    break;
                case product_compare_print_model_1.ProductCompareRowType.availableInventoryCases:
                    value = printProductCompareModel?.availableInventoryCases;
                    break;
                case product_compare_print_model_1.ProductCompareRowType.availableInventoryEaches:
                    value = printProductCompareModel?.availableInventoryEaches;
                    break;
                case product_compare_print_model_1.ProductCompareRowType.productAttributes:
                    if (printProductCompareModel?.productStatusPills?.length > 0) {
                        value = "";
                    }
                    break;
                case product_compare_print_model_1.ProductCompareRowType.purchaseHistory:
                    value = printProductCompareModel?.purchaseHistory;
                    break;
                case product_compare_print_model_1.ProductCompareRowType.netWeight:
                    if (!!printProductCompareModel?.productDetail?.detail?.netWeight) {
                        value =
                            printProductCompareModel?.productDetail?.detail?.netWeight +
                                " " +
                                printProductCompareModel?.productDetail?.detail?.weightUom;
                    }
                    break;
                case product_compare_print_model_1.ProductCompareRowType.usfStorage:
                    if (!!printProductCompareModel?.productDetail?.detail
                        ?.storageCodeDescription) {
                        value =
                            printProductCompareModel?.productDetail?.detail
                                ?.storageCodeDescription +
                                " " +
                                printProductCompareModel?.productDetail?.summary
                                    ?.productStorageCode;
                    }
                    break;
                case product_compare_print_model_1.ProductCompareRowType.ingredients:
                    value = printProductCompareModel?.productDetail?.detail?.ingredients;
                    break;
                case product_compare_print_model_1.ProductCompareRowType.nutritionalFacts:
                    value = "";
                    break;
                case product_compare_print_model_1.ProductCompareRowType.servingSize:
                    if (!printProductCompareModel?.productDetail?.nutritionFacts
                        ?.nutrientIsNotAvailable) {
                        value =
                            printProductCompareModel?.productDetail?.nutritionFacts
                                ?.servingSizeData;
                    }
                    break;
                case product_compare_print_model_1.ProductCompareRowType.servingsPerContainer:
                    if (!printProductCompareModel?.productDetail?.nutritionFacts
                        ?.nutrientIsNotAvailable) {
                        value =
                            printProductCompareModel?.productDetail?.detail?.servingsPerCase;
                    }
                    break;
                case product_compare_print_model_1.ProductCompareRowType.calories:
                    if (!printProductCompareModel?.productDetail?.nutritionFacts
                        ?.nutrientIsNotAvailable) {
                        value = `${printProductCompareModel?.productDetail?.nutritionFacts?.calories}`;
                    }
                    break;
                case product_compare_print_model_1.ProductCompareRowType.caloriesFromFat:
                    if (!printProductCompareModel?.productDetail?.nutritionFacts
                        ?.nutrientIsNotAvailable &&
                        printProductCompareModel?.productDetail?.isUsda) {
                        value = `${printProductCompareModel?.productDetail?.nutritionFacts?.caloriesFromFat}`;
                    }
                    break;
                case product_compare_print_model_1.ProductCompareRowType.nutrient:
                    if (!printProductCompareModel?.productDetail?.nutritionFacts
                        ?.nutrientIsNotAvailable) {
                        const nutrients = printProductCompareModel?.productDetail?.nutritionFacts
                            ?.nutrientsData;
                        const currentNutrient = nutrients?.find((nutrient) => nutrient?.data?.nutrientName === row?.header);
                        if (currentNutrient) {
                            const nutrient = currentNutrient?.data;
                            if (printProductCompareModel?.productDetail?.isUsda &&
                                product_page_print_constants_1.USDA_PERCENTAGE_ROWS.includes(row?.header)) {
                                value =
                                    nutrient?.nutrientRdvPercentage !== null &&
                                        nutrient?.nutrientRdvPercentage !== undefined
                                        ? `${nutrient?.nutrientRdvPercentage}%`
                                        : "0%";
                            }
                            else {
                                value =
                                    nutrient?.nutrientValue !== null &&
                                        nutrient?.nutrientValue >= 0
                                        ? `${nutrient?.nutrientValue} ${nutrient?.nutrientUom}`
                                        : "";
                            }
                        }
                    }
                    break;
            }
            row?.values?.push(value ?? "N/A");
        });
    });
};
const createTableBody = (documentRows) => documentRows?.map((row) => {
    const header = {
        content: row?.header,
        styles: { ...row?.rowStyles, ...row?.headerStyles },
    };
    const values = row?.values?.map((value) => ({
        content: value,
        styles: { ...row?.rowStyles, ...row?.valueStyles },
    }));
    values?.unshift(header);
    return values;
});
const addProductCompareTable = (doc, tableHeader, tableBody, productPills, productPillImageMap, currentX, currentY, columnStyles, columnWidth) => {
    const { cellPadding, halign } = product_page_print_constants_1.PRODUCT_COMPARE_TABLE_STYLES;
    const productAttributesRow = 9;
    const maxPillCount = productPills?.reduce((maxLength, currentValue) => Math.max(maxLength, currentValue?.length), 0);
    let pillsIndex = 0;
    (0, jspdf_autotable_1.default)(doc, {
        theme: product_page_print_constants_1.JSPDF_TYPE_PLAIN,
        head: tableHeader,
        body: tableBody,
        startY: currentY,
        margin: {
            top: product_page_print_constants_1.HEADER_HEIGHT,
            left: currentX,
            bottom: product_page_print_constants_1.FOOTER_HEIGHT,
        },
        tableWidth: product_page_print_constants_1.PAGE_CONTENT_WIDTH,
        columnStyles,
        styles: {
            ...product_page_print_constants_1.JSPDF_TABLE_BASE_STYLES,
            ...product_page_print_constants_1.PRODUCT_COMPARE_TABLE_STYLES,
        },
        headStyles: {
            fillColor: product_page_print_constants_1.GRAY,
            halign: halign,
            textColor: product_page_print_constants_1.WHITE,
        },
        rowPageBreak: "avoid",
        didParseCell: (data) => {
            if (data.column.index === 0) {
                data.cell.styles.halign = "left";
            }
            if (data.row.index === productAttributesRow) {
                data.cell.styles.minCellHeight = maxPillCount * 20 + cellPadding * 2;
            }
        },
        didDrawCell: (data) => {
            if (data.row.index === productAttributesRow &&
                data.column.index !== 0 &&
                pillsIndex < productPills?.length) {
                addProductPills(doc, productPills?.[pillsIndex], productPillImageMap, data.cursor.x, data.cursor.y, columnWidth);
                pillsIndex++;
            }
        },
        didDrawPage: (data) => {
            currentY = data.cursor.y;
        },
    });
    return [currentX, currentY];
};
const addNutritionTable = (doc, tableHeader, tableBody, currentX, currentY, columnStyles) => {
    const startY = currentY;
    (0, jspdf_autotable_1.default)(doc, {
        theme: product_page_print_constants_1.JSPDF_TYPE_PLAIN,
        head: tableHeader,
        body: tableBody,
        startY: startY,
        margin: {
            top: product_page_print_constants_1.HEADER_HEIGHT,
            left: currentX,
            bottom: product_page_print_constants_1.FOOTER_HEIGHT,
        },
        tableWidth: product_page_print_constants_1.PAGE_CONTENT_WIDTH,
        headStyles: {
            fillColor: product_page_print_constants_1.GRAY,
            halign: product_page_print_constants_1.PRODUCT_COMPARE_TABLE_STYLES.halign,
            textColor: product_page_print_constants_1.WHITE,
        },
        columnStyles,
        styles: {
            ...product_page_print_constants_1.JSPDF_TABLE_BASE_STYLES,
            ...product_page_print_constants_1.PRODUCT_COMPARE_TABLE_STYLES,
        },
        pageBreak: "avoid",
        willDrawPage: (data) => {
            data.settings.showHead = data.cursor.y !== startY ? "everyPage" : "never";
        },
        didParseCell: (data) => {
            if (data.column.index === 0) {
                data.cell.styles.halign = "left";
            }
        },
    });
    return [currentX, currentY];
};
const addProductPills = async (doc, pills, productPillImageMap, currentX, currentY, columnWidth) => {
    const { cellPadding } = product_page_print_constants_1.PRODUCT_COMPARE_TABLE_STYLES;
    currentY += cellPadding;
    const { pillHeight, pillWidth, pillVerticalPadding } = product_page_print_constants_1.PRODUCT_COMPARE_DIMENSIONS;
    for (const pill of pills) {
        const img = productPillImageMap?.get(pill);
        if (!!img) {
            const pillX = currentX + (columnWidth - pillWidth) / 2;
            doc.addImage(img, "png", pillX, currentY, pillWidth, pillHeight);
            currentY += pillHeight + pillVerticalPadding;
        }
    }
};
const addFooter = (doc, pageNumber, pageCount, currentX, currentY) => {
    doc.setDrawColor(product_page_print_constants_1.GRAY);
    doc.setLineWidth(product_page_print_constants_1.LINE_WIDTH);
    doc.line(currentX, currentY, product_page_print_constants_1.PAGE_WIDTH, currentY);
    currentX += product_page_print_constants_1.PAGE_MARGIN;
    currentY += product_page_print_constants_1.PADDING / 2;
    const disclaimer = "All product information contained herein is subject " +
        "to change, including pricing, promotions, and " +
        "nutritionals. For current information at the time " +
        "of purchase, please contact your sales associate.";
    const disclaimerTextWidth = product_page_print_constants_1.PAGE_CONTENT_WIDTH;
    [currentX, currentY] = (0, exports.addMultiLineText)(doc, disclaimer, currentX, currentY, disclaimerTextWidth, product_page_print_constants_1.PARAGRAPH_FONT_SIZE - 2, "normal", "#585858");
    currentY += product_page_print_constants_1.PADDING;
    doc.line(currentX, currentY, product_page_print_constants_1.PAGE_WIDTH - product_page_print_constants_1.PAGE_MARGIN, currentY);
    currentY += product_page_print_constants_1.PARAGRAPH_FONT_SIZE + product_page_print_constants_1.PADDING / 2;
    doc.setTextColor(product_page_print_constants_1.BLACK);
    doc.setFontSize(product_page_print_constants_1.PARAGRAPH_FONT_SIZE);
    doc.text("For the exclusive use by US Foods customers", currentX, currentY);
    const date = `Print Date: ${new Date().toLocaleDateString("en-US", {
        month: "numeric",
        day: "numeric",
        year: "2-digit",
    })}`;
    const page = `Page ${pageNumber} of ${pageCount}`;
    const padding = product_page_print_constants_1.PADDING * 3;
    const dateAndPageTextWidth = doc.getTextWidth(date) + doc.getTextWidth(page) + padding * 2;
    currentX = product_page_print_constants_1.PAGE_WIDTH - product_page_print_constants_1.PAGE_MARGIN - dateAndPageTextWidth;
    currentY -= product_page_print_constants_1.PARAGRAPH_FONT_SIZE - 2;
    addSeparatedText(doc, [date, page], currentX, currentY, padding);
};
exports.addFooter = addFooter;
const addSeparatedText = (doc, text, currentX, currentY, padding = product_page_print_constants_1.PADDING, addSeparator = true) => {
    (0, jspdf_autotable_1.default)(doc, {
        theme: product_page_print_constants_1.JSPDF_TYPE_PLAIN,
        body: [text],
        startY: currentY,
        margin: {
            left: currentX,
            bottom: 0,
        },
        tableWidth: "wrap",
        styles: {
            ...product_page_print_constants_1.JSPDF_TABLE_BASE_STYLES,
            halign: "center",
            cellPadding: {
                top: 0,
                bottom: 0,
                left: padding,
                right: padding,
            },
            lineWidth: {
                right: addSeparator ? product_page_print_constants_1.LINE_WIDTH : 0,
            },
            lineColor: product_page_print_constants_1.GRAY,
        },
        didParseCell: (data) => {
            if (data.column.index === 0) {
                data.cell.styles.cellPadding = {
                    left: 0,
                    right: padding,
                };
            }
            if (data.column.index === text.length - 1) {
                data.cell.styles.lineWidth = 0;
            }
        },
        didDrawPage: (data) => {
            currentY = data.cursor.y;
        },
    });
    return [currentX, currentY];
};
